import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/lib/i18n";
import { useSeoMeta } from "@/lib/useSeoMeta";
import { orgAndWebSite, breadcrumbList, ORIGIN, CONTENT_LAST_MODIFIED } from "@/lib/schemaOrg";
import { LABEL_COUNTRIES, DIRECTORY_BASE, COUNTRY_CODES, countryBySlug } from "@/constants/labelCountries";
import { ArrowRight, ShieldCheck, AlertTriangle, XCircle, Globe2, ExternalLink, Search } from "lucide-react";

const LANGS = Object.keys(DIRECTORY_BASE);

const DIR_T = {
  fr: {
    eyebrow: "Annuaire officiel", crumb: "Labels environnementaux",
    idxTitle: "Labels environnementaux reconnus dans l'UE-27",
    idxDesc: "Annuaire des {n} labels et certifications environnementales des 27 États membres, qualifiés au regard de la directive (UE) 2024/825 sur les allégations environnementales.",
    idxIntro: "Depuis le 27 septembre 2026, un label de durabilité ne peut être affiché que s'il repose sur un système de certification tiers ou s'il est établi par une autorité publique (annexe I, point 2 bis). Cet annuaire recense les labels utilisables pays par pays et indique lesquels sont recevables comme preuve.",
    pick: "Choisissez un pays",
    cTitle: "Labels environnementaux en {c} — directive UE 2024/825",
    cDesc: "{n} labels et certifications environnementales utilisables en {c}, dont {e} recevables comme preuve au titre de la directive (UE) 2024/825.",
    cIntro: "Labels nationaux, européens et internationaux mobilisables dans une allégation environnementale en {c}, avec leur statut de recevabilité au regard de la directive (UE) 2024/825.",
    total: "labels", eligible: "recevables comme preuve", national: "labels nationaux", toCheck: "à vérifier",
    thLabel: "Label", thScope: "Portée", thCat: "Catégorie", thType: "Type", thIssuer: "Organisme", thStatus: "Recevabilité",
    stOk: "Recevable", stPartial: "Sous réserve", stNo: "Non recevable", stOut: "Hors champ",
    guarantees: "Garantit", watch: "Point de vigilance",
    ctaTitle: "Vos allégations sont-elles couvertes par vos labels ?",
    ctaText: "Scannez votre site gratuitement : EmpCo détecte vos allégations environnementales, les qualifie au regard de la directive et vérifie si vos certifications vous donnent le droit de les écrire.",
    cta: "Scanner mon site gratuitement", back: "Tous les pays",
    loading: "Chargement du référentiel…",
    loadError: "Référentiel momentanément indisponible. Réessayez dans quelques instants.",
    updated: "Référentiel mis à jour le",
  },
  en: {
    eyebrow: "Official directory", crumb: "Environmental labels",
    idxTitle: "Environmental labels recognised across the EU-27",
    idxDesc: "Directory of {n} environmental labels and certifications across the 27 Member States, assessed against Directive (EU) 2024/825 on environmental claims.",
    idxIntro: "Since 27 September 2026, a sustainability label may only be displayed if it is based on a third-party certification scheme or established by a public authority (Annex I, point 2a). This directory lists the labels usable country by country and which ones qualify as evidence.",
    pick: "Choose a country",
    cTitle: "Environmental labels in {c} — EU Directive 2024/825",
    cDesc: "{n} environmental labels and certifications usable in {c}, including {e} that qualify as evidence under Directive (EU) 2024/825.",
    cIntro: "National, European and international labels that can back an environmental claim in {c}, with their admissibility status under Directive (EU) 2024/825.",
    total: "labels", eligible: "qualify as evidence", national: "national labels", toCheck: "to be checked",
    thLabel: "Label", thScope: "Scope", thCat: "Category", thType: "Type", thIssuer: "Body", thStatus: "Admissibility",
    stOk: "Admissible", stPartial: "Conditional", stNo: "Not admissible", stOut: "Out of scope",
    guarantees: "Guarantees", watch: "Watch out",
    ctaTitle: "Are your claims actually covered by your labels?",
    ctaText: "Scan your website for free: EmpCo detects your environmental claims, qualifies them against the directive and checks whether your certifications give you the right to make them.",
    cta: "Scan my website for free", back: "All countries",
    loading: "Loading the reference data…",
    loadError: "Reference data temporarily unavailable. Please try again shortly.",
    updated: "Reference data updated on",
  },
  de: {
    eyebrow: "Offizielles Verzeichnis", crumb: "Umweltlabels",
    idxTitle: "In der EU-27 anerkannte Umweltlabels",
    idxDesc: "Verzeichnis von {n} Umweltlabels und Zertifizierungen in den 27 Mitgliedstaaten, bewertet nach Richtlinie (EU) 2024/825 über Umweltaussagen.",
    idxIntro: "Seit dem 27. September 2026 darf ein Nachhaltigkeitssiegel nur angezeigt werden, wenn es auf einem Zertifizierungssystem Dritter beruht oder von einer Behörde eingeführt wurde (Anhang I, Nr. 2a). Dieses Verzeichnis listet die je Land nutzbaren Siegel und ihre Beweiskraft auf.",
    pick: "Land auswählen",
    cTitle: "Umweltlabels in {c} — EU-Richtlinie 2024/825",
    cDesc: "{n} in {c} nutzbare Umweltlabels und Zertifizierungen, davon {e} als Nachweis nach Richtlinie (EU) 2024/825 zulässig.",
    cIntro: "Nationale, europäische und internationale Siegel, die in {c} eine Umweltaussage stützen können, mit ihrem Zulässigkeitsstatus nach Richtlinie (EU) 2024/825.",
    total: "Siegel", eligible: "als Nachweis zulässig", national: "nationale Siegel", toCheck: "zu prüfen",
    thLabel: "Siegel", thScope: "Geltungsbereich", thCat: "Kategorie", thType: "Art", thIssuer: "Stelle", thStatus: "Zulässigkeit",
    stOk: "Zulässig", stPartial: "Bedingt", stNo: "Nicht zulässig", stOut: "Außerhalb",
    guarantees: "Garantiert", watch: "Achtung",
    ctaTitle: "Sind Ihre Aussagen von Ihren Siegeln gedeckt?",
    ctaText: "Scannen Sie Ihre Website kostenlos: EmpCo erkennt Ihre Umweltaussagen, qualifiziert sie nach der Richtlinie und prüft, ob Ihre Zertifizierungen sie rechtfertigen.",
    cta: "Website kostenlos scannen", back: "Alle Länder",
    loading: "Referenzdaten werden geladen…",
    loadError: "Referenzdaten vorübergehend nicht verfügbar. Bitte später erneut versuchen.",
    updated: "Referenzdaten aktualisiert am",
  },
  es: {
    eyebrow: "Directorio oficial", crumb: "Etiquetas ambientales",
    idxTitle: "Etiquetas ambientales reconocidas en la UE-27",
    idxDesc: "Directorio de {n} etiquetas y certificaciones ambientales de los 27 Estados miembros, evaluadas conforme a la Directiva (UE) 2024/825 sobre alegaciones ambientales.",
    idxIntro: "Desde el 27 de septiembre de 2026, una etiqueta de sostenibilidad solo puede mostrarse si se basa en un sistema de certificación de terceros o si la establece una autoridad pública (anexo I, punto 2 bis). Este directorio recoge las etiquetas utilizables país por país y su valor probatorio.",
    pick: "Elija un país",
    cTitle: "Etiquetas ambientales en {c} — Directiva UE 2024/825",
    cDesc: "{n} etiquetas y certificaciones ambientales utilizables en {c}, de las cuales {e} son admisibles como prueba según la Directiva (UE) 2024/825.",
    cIntro: "Etiquetas nacionales, europeas e internacionales que pueden respaldar una alegación ambiental en {c}, con su estado de admisibilidad conforme a la Directiva (UE) 2024/825.",
    total: "etiquetas", eligible: "admisibles como prueba", national: "etiquetas nacionales", toCheck: "por verificar",
    thLabel: "Etiqueta", thScope: "Alcance", thCat: "Categoría", thType: "Tipo", thIssuer: "Organismo", thStatus: "Admisibilidad",
    stOk: "Admisible", stPartial: "Condicional", stNo: "No admisible", stOut: "Fuera de ámbito",
    guarantees: "Garantiza", watch: "Atención",
    ctaTitle: "¿Sus alegaciones están cubiertas por sus etiquetas?",
    ctaText: "Escanee su sitio gratis: EmpCo detecta sus alegaciones ambientales, las cualifica conforme a la directiva y comprueba si sus certificaciones le dan derecho a utilizarlas.",
    cta: "Escanear mi sitio gratis", back: "Todos los países",
    loading: "Cargando el referencial…",
    loadError: "Referencial temporalmente no disponible. Inténtelo de nuevo en unos instantes.",
    updated: "Referencial actualizado el",
  },
  it: {
    eyebrow: "Elenco ufficiale", crumb: "Etichette ambientali",
    idxTitle: "Etichette ambientali riconosciute nell'UE-27",
    idxDesc: "Elenco di {n} etichette e certificazioni ambientali dei 27 Stati membri, valutate alla luce della direttiva (UE) 2024/825 sulle dichiarazioni ambientali.",
    idxIntro: "Dal 27 settembre 2026 un'etichetta di sostenibilità può essere esposta solo se basata su un sistema di certificazione di terzi o istituita da un'autorità pubblica (allegato I, punto 2 bis). Questo elenco raccoglie le etichette utilizzabili paese per paese e il loro valore probatorio.",
    pick: "Scegli un paese",
    cTitle: "Etichette ambientali in {c} — direttiva UE 2024/825",
    cDesc: "{n} etichette e certificazioni ambientali utilizzabili in {c}, di cui {e} ammissibili come prova ai sensi della direttiva (UE) 2024/825.",
    cIntro: "Etichette nazionali, europee e internazionali utilizzabili a sostegno di una dichiarazione ambientale in {c}, con il loro stato di ammissibilità ai sensi della direttiva (UE) 2024/825.",
    total: "etichette", eligible: "ammissibili come prova", national: "etichette nazionali", toCheck: "da verificare",
    thLabel: "Etichetta", thScope: "Ambito", thCat: "Categoria", thType: "Tipo", thIssuer: "Organismo", thStatus: "Ammissibilità",
    stOk: "Ammissibile", stPartial: "Con riserva", stNo: "Non ammissibile", stOut: "Fuori ambito",
    guarantees: "Garantisce", watch: "Attenzione",
    ctaTitle: "Le tue dichiarazioni sono coperte dalle tue etichette?",
    ctaText: "Scansiona il tuo sito gratuitamente: EmpCo rileva le tue dichiarazioni ambientali, le qualifica secondo la direttiva e verifica se le tue certificazioni ti danno il diritto di usarle.",
    cta: "Scansiona il mio sito gratis", back: "Tutti i paesi",
    loading: "Caricamento del referenziale…",
    loadError: "Referenziale momentaneamente non disponibile. Riprova tra qualche istante.",
    updated: "Dati di riferimento aggiornati il",
  },
  nl: {
    eyebrow: "Officiële gids", crumb: "Milieulabels",
    idxTitle: "In de EU-27 erkende milieulabels",
    idxDesc: "Gids met {n} milieulabels en certificeringen in de 27 lidstaten, getoetst aan Richtlijn (EU) 2024/825 over milieuclaims.",
    idxIntro: "Sinds 27 september 2026 mag een duurzaamheidslabel alleen worden weergegeven als het berust op een certificeringsregeling van derden of is vastgesteld door een openbare autoriteit (bijlage I, punt 2 bis). Deze gids toont per land de bruikbare labels en hun bewijswaarde.",
    pick: "Kies een land",
    cTitle: "Milieulabels in {c} — EU-richtlijn 2024/825",
    cDesc: "{n} milieulabels en certificeringen bruikbaar in {c}, waarvan {e} toelaatbaar als bewijs onder Richtlijn (EU) 2024/825.",
    cIntro: "Nationale, Europese en internationale labels die een milieuclaim in {c} kunnen onderbouwen, met hun toelaatbaarheid onder Richtlijn (EU) 2024/825.",
    total: "labels", eligible: "toelaatbaar als bewijs", national: "nationale labels", toCheck: "te controleren",
    thLabel: "Label", thScope: "Bereik", thCat: "Categorie", thType: "Type", thIssuer: "Instantie", thStatus: "Toelaatbaarheid",
    stOk: "Toelaatbaar", stPartial: "Onder voorbehoud", stNo: "Niet toelaatbaar", stOut: "Buiten bereik",
    guarantees: "Garandeert", watch: "Let op",
    ctaTitle: "Worden uw claims gedekt door uw labels?",
    ctaText: "Scan uw website gratis: EmpCo detecteert uw milieuclaims, kwalificeert ze volgens de richtlijn en controleert of uw certificeringen u het recht geven ze te gebruiken.",
    cta: "Mijn website gratis scannen", back: "Alle landen",
    loading: "Referentiegegevens laden…",
    loadError: "Referentiegegevens tijdelijk niet beschikbaar. Probeer het straks opnieuw.",
    updated: "Referentiegegevens bijgewerkt op",
  },
};

const tr = (lang) => DIR_T[lang] || DIR_T.en;
const fill = (s, vars) => Object.entries(vars).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, v), s);

const indexUrl = (lang) => `${ORIGIN}${DIRECTORY_BASE[lang]}`;
const countryUrl = (lang, code) => `${ORIGIN}${DIRECTORY_BASE[lang]}/${LABEL_COUNTRIES[lang][code].slug}`;

function alternates(code) {
  const alts = LANGS.map((l) => ({ hreflang: l, href: code ? countryUrl(l, code) : indexUrl(l) }));
  alts.push({ hreflang: "x-default", href: code ? countryUrl("fr", code) : indexUrl("fr") });
  return alts;
}

/** Au montage : aligne la langue de l'app sur la route. Ensuite, un changement de langue
 *  via le sélecteur du header redirige vers l'URL localisée équivalente. */
function useDirectoryLang(routeLang, code) {
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      if (routeLang && routeLang !== lang) setLang(routeLang);
      return;
    }
    if (lang !== routeLang && DIRECTORY_BASE[lang]) {
      navigate(code ? `${DIRECTORY_BASE[lang]}/${LABEL_COUNTRIES[lang][code].slug}` : DIRECTORY_BASE[lang]);
    }
  }, [lang, routeLang, code, setLang, navigate]);
  return routeLang || lang;
}

const STATUS = {
  oui: { k: "stOk", cls: "tag-green", Icon: ShieldCheck },
  sous_reserve: { k: "stPartial", cls: "tag-orange", Icon: AlertTriangle },
  hors_champ: { k: "stOut", cls: "tag-orange", Icon: AlertTriangle },
  non: { k: "stNo", cls: "tag-red", Icon: XCircle },
};

function Cta({ lang }) {
  const T = tr(lang);
  return (
    <section className="hairline p-8 md:p-10 mt-16" data-testid="labels-cta">
      <h2 className="text-lg md:text-lg font-serif">{T.ctaTitle}</h2>
      <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed text-sm">{T.ctaText}</p>
      <Link to="/" className="btn-primary inline-flex mt-6" data-testid="labels-cta-link">
        <Search className="w-4 h-4" /> {T.cta} <ArrowRight className="w-4 h-4" />
      </Link>
    </section>
  );
}

export function LabelsDirectoryIndex({ lang: routeLang }) {
  const L = useDirectoryLang(routeLang, null);
  const T = tr(L);
  const [stats, setStats] = useState([]);
  const [total, setTotal] = useState(1805);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/labels/directory")
      .then(r => { setStats(r.data.countries || []); setTotal(r.data.total_labels || 0); })
      .catch(() => setFailed(true));
  }, []);

  const byCode = Object.fromEntries(stats.map(s => [s.code, s]));
  const names = LABEL_COUNTRIES[L] || LABEL_COUNTRIES.en;
  const codes = [...COUNTRY_CODES].sort((a, b) => names[a].name.localeCompare(names[b].name));

  useSeoMeta({
    title: `${T.idxTitle} | EmpCo`,
    description: fill(T.idxDesc, { n: total }),
    canonical: indexUrl(L),
    alternates: alternates(null),
    jsonLd: [
      orgAndWebSite(L),
      breadcrumbList([{ name: "EmpCo", url: ORIGIN }, { name: T.crumb, url: indexUrl(L) }]),
      {
        "@context": "https://schema.org", "@type": "CollectionPage",
        name: T.idxTitle, description: fill(T.idxDesc, { n: total }),
        url: indexUrl(L), inLanguage: L, dateModified: CONTENT_LAST_MODIFIED,
        hasPart: codes.map((c) => ({
          "@type": "WebPage", name: fill(T.cTitle, { c: names[c].name }), url: countryUrl(L, c),
        })),
      },
    ],
  });

  return (
    <div className="App min-h-screen">
      <Header variant="public" />
      <section className="brand-mesh border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <p className="eyebrow mb-3">{T.eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl" data-testid="labels-index-title">{T.idxTitle}</h1>
          <p className="mt-5 text-muted-foreground max-w-3xl leading-relaxed">{T.idxIntro}</p>
          <p className="mt-4 font-mono text-xs text-muted-foreground">{T.updated} {CONTENT_LAST_MODIFIED} · {total} {T.total}</p>
        </div>
      </section>
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <h2 className="text-lg md:text-lg font-serif mb-6">{T.pick}</h2>
        {failed && <p className="text-sm text-[hsl(var(--status-red))] mb-4" data-testid="labels-index-error">{T.loadError}</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="labels-country-grid">
          {codes.map((c) => {
            const s = byCode[c];
            return (
              <Link key={c} to={`${DIRECTORY_BASE[L]}/${names[c].slug}`}
                className="hairline card-lift p-6 flex items-center justify-between gap-4"
                data-testid={`labels-country-card-${c}`}>
                <div>
                  <p className="font-serif text-lg flex items-center gap-2"><Globe2 className="w-4 h-4 text-primary" /> {names[c].name}</p>
                  {s && (
                    <p className="font-mono text-[11px] text-muted-foreground mt-1.5">
                      {s.total} {T.total} · <span className="text-[hsl(var(--status-green))]">{s.eligible} {T.eligible}</span>
                    </p>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </Link>
            );
          })}
        </div>
        <Cta lang={L} />
      </main>
      <Footer />
    </div>
  );
}

export function LabelsCountryPage({ lang: routeLang }) {
  const { slug } = useParams();
  const { lang: ctxLang } = useLang();
  const guessLang = routeLang || ctxLang;
  const code = countryBySlug(guessLang, slug) || countryBySlug("fr", slug);
  const L = useDirectoryLang(routeLang, code);
  const T = tr(L);
  const names = LABEL_COUNTRIES[L] || LABEL_COUNTRIES.en;
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!code) { setLoading(false); return; }
    setLoading(true);
    api.get("/labels", { params: { country: code } })
      .then(r => setLabels(r.data.labels || []))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [code]);

  const cname = code ? names[code].name : slug;
  const eligible = labels.filter(l => l.eligible === "oui").length;
  const national = labels.filter(l => (l.scope || "").toLowerCase().startsWith("national")).length;
  const toCheck = labels.filter(l => l.eligible === "sous_reserve").length;

  useSeoMeta({
    title: `${fill(T.cTitle, { c: cname })} | EmpCo`,
    description: fill(T.cDesc, { n: labels.length || "", c: cname, e: eligible }),
    canonical: code ? countryUrl(L, code) : undefined,
    robots: code ? "index, follow" : "noindex, follow",
    alternates: code ? alternates(code) : undefined,
    jsonLd: code ? [
      orgAndWebSite(L),
      breadcrumbList([
        { name: "EmpCo", url: ORIGIN },
        { name: T.crumb, url: indexUrl(L) },
        { name: cname, url: countryUrl(L, code) },
      ]),
      {
        "@context": "https://schema.org", "@type": "DefinedTermSet",
        name: fill(T.cTitle, { c: cname }), url: countryUrl(L, code), inLanguage: L,
        dateModified: CONTENT_LAST_MODIFIED,
        hasDefinedTerm: labels.slice(0, 60).map((l) => ({
          "@type": "DefinedTerm", name: l.label,
          description: [l.guarantees, l.category, l.label_type].filter(Boolean).join(" · "),
          ...(l.official_url ? { url: l.official_url } : {}),
          ...(l.issuer ? { inDefinedTermSet: { "@type": "DefinedTermSet", name: l.issuer } } : {}),
        })),
      },
    ] : undefined,
  });

  if (!code) {
    return (
      <div className="App min-h-screen">
        <Header variant="public" />
        <main className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="labels-country-notfound">
          <p className="text-muted-foreground">404</p>
          <Link to={DIRECTORY_BASE[L]} className="btn-primary inline-flex mt-6">{T.back}</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App min-h-screen">
      <Header variant="public" />
      <section className="brand-mesh border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <nav className="font-mono text-[11px] text-muted-foreground mb-4">
            <Link to={DIRECTORY_BASE[L]} className="link-underline" data-testid="labels-breadcrumb">{T.crumb}</Link> / {cname}
          </nav>
          <p className="eyebrow mb-3">{T.eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl" data-testid="labels-country-title">
            {fill(T.cTitle, { c: cname })}
          </h1>
          <p className="mt-5 text-muted-foreground max-w-3xl leading-relaxed">{fill(T.cIntro, { c: cname })}</p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            <Stat n={labels.length} l={T.total} testid="labels-stat-total" />
            <Stat n={eligible} l={T.eligible} tone="green" testid="labels-stat-eligible" />
            <Stat n={national} l={T.national} testid="labels-stat-national" />
            <Stat n={toCheck} l={T.toCheck} tone="orange" testid="labels-stat-tocheck" />
          </div>
        </div>
      </section>
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <div className="hairline overflow-x-auto" data-testid="labels-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-border text-left">
                {[T.thLabel, T.thScope, T.thCat, T.thType, T.thIssuer, T.thStatus].map(h => (
                  <th key={h} className="p-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(loading || failed) && (
                <tr><td colSpan={6} className="p-6 text-sm text-muted-foreground" data-testid="labels-table-state">
                  {failed ? T.loadError : T.loading}
                </td></tr>
              )}
              {labels.map((l) => {
                const st = STATUS[l.eligible] || STATUS.sous_reserve;
                return (
                  <tr key={`${l.country}-${l.label}`} className="border-b border-border last:border-0 align-top" data-testid={`labels-row-${l.label}`}>
                    <td className="p-4">
                      <p className="font-serif text-base">{l.label}</p>
                      {l.guarantees && <p className="text-xs text-muted-foreground mt-1">{T.guarantees} · {l.guarantees}</p>}
                      {l.watchpoints && <p className="text-xs text-[hsl(29_87%_40%)] mt-1">{T.watch} · {l.watchpoints}</p>}
                      {l.official_url && (
                        <a href={l.official_url} target="_blank" rel="noreferrer nofollow" className="text-[11px] text-primary link-underline inline-flex items-center gap-1 mt-1">
                          {l.official_url.replace(/^https?:\/\//, "").slice(0, 40)} <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </td>
                    <td className="p-4 font-mono text-[11px] text-muted-foreground">{l.scope}</td>
                    <td className="p-4 font-mono text-[11px] text-muted-foreground">{l.category}</td>
                    <td className="p-4 font-mono text-[11px] text-muted-foreground">{l.label_type}</td>
                    <td className="p-4 text-xs text-muted-foreground">{l.issuer}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-lg font-mono text-[10px] inline-flex items-center gap-1 whitespace-nowrap ${st.cls}`}>
                        <st.Icon className="w-3 h-3" /> {T[st.k]}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-4 font-mono text-xs text-muted-foreground">{T.updated} {CONTENT_LAST_MODIFIED}</p>
        <Cta lang={L} />
      </main>
      <Footer />
    </div>
  );
}

function Stat({ n, l, tone, testid }) {
  const color = tone === "green" ? "text-[hsl(var(--status-green))]" : tone === "orange" ? "text-[hsl(29_87%_50%)]" : "";
  return (
    <div className="hairline p-5" data-testid={testid}>
      <p className={`text-3xl font-serif ${color}`}>{n}</p>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{l}</p>
    </div>
  );
}
